
import { useState, useEffect } from "react";
import { companyAPI } from "@/services/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MoreHorizontal,
  Edit,
  Eye,
  Building2,
  Users,
  FileText,
  CreditCard,
} from "lucide-react";

const CompaniesManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    subscriptionPlan: "",
    subscriptionStatus: "",
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await companyAPI.getAll();
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to load companies");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (company) => {
    setSelectedCompany(company);
    setEditFormData({
      name: company.name,
      email: company.email,
      subscriptionPlan: company.subscriptionPlan,
      subscriptionStatus: company.subscriptionStatus,
    });
    setIsEditOpen(true);
  };

  const handleDetailsClick = (company) => {
    setSelectedCompany(company);
    setIsDetailsOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await companyAPI.update(selectedCompany._id, editFormData);
      toast.success("Company updated successfully");
      setIsEditOpen(false);
      fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error("Failed to update company");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubscriptionChange = (value) => {
    setEditFormData((prev) => ({
      ...prev,
      subscriptionPlan: value,
    }));
  };

  const handleStatusChange = (value) => {
    setEditFormData((prev) => ({
      ...prev,
      subscriptionStatus: value,
    }));
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format subscription status with badge
  const SubscriptionBadge = ({ status }) => {
    let bgColor;
    switch (status?.toLowerCase()) {
      case "active":
        bgColor = "bg-green-100 text-green-800";
        break;
      case "expired":
        bgColor = "bg-red-100 text-red-800";
        break;
      case "trial":
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case "pending":
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
    }

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bgColor}`}
      >
        {status || "Unknown"}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Companies</h1>
        <p className="text-muted-foreground">
          Manage companies registered on the platform
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Companies</CardTitle>
          <CardDescription>
            View and manage all registered companies
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registered On</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-24 text-center"
                      >
                        No companies found
                      </TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => (
                      <TableRow key={company._id}>
                        <TableCell className="font-medium">
                          {company.name}
                        </TableCell>
                        <TableCell>{company.email}</TableCell>
                        <TableCell>
                          {formatDate(company.createdAt)}
                        </TableCell>
                        <TableCell>
                          {company.subscriptionPlan || "No Plan"}
                        </TableCell>
                        <TableCell>
                          <SubscriptionBadge
                            status={company.subscriptionStatus}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-8 w-8 p-0"
                              >
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleDetailsClick(company)}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleEditClick(company)}
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Company
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Company Details</DialogTitle>
            <DialogDescription>
              View detailed information about the company
            </DialogDescription>
          </DialogHeader>
          {selectedCompany && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company Name</Label>
                  <p className="font-medium">{selectedCompany.name}</p>
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <p className="font-medium">{selectedCompany.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Subscription Plan</Label>
                  <p className="font-medium">
                    {selectedCompany.subscriptionPlan || "No Plan"}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <div>
                    <SubscriptionBadge
                      status={selectedCompany.subscriptionStatus}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Registered On</Label>
                  <p className="font-medium">
                    {formatDate(selectedCompany.createdAt)}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label>Subscription Ends</Label>
                  <p className="font-medium">
                    {formatDate(selectedCompany.subscriptionEnd)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <Card className="col-span-1">
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm">Admins</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-xl font-bold flex items-center">
                      <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                      {selectedCompany.adminCount || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="col-span-1">
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm">Candidates</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-xl font-bold flex items-center">
                      <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                      {selectedCompany.candidateCount || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="col-span-1">
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm">Exams</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-xl font-bold flex items-center">
                      <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                      {selectedCompany.examCount || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="col-span-1">
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm">Revenue</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-xl font-bold flex items-center">
                      <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                      ${selectedCompany.totalRevenue || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company information and subscription details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscriptionPlan">Subscription Plan</Label>
                <Select
                  onValueChange={handleSubscriptionChange}
                  value={editFormData.subscriptionPlan}
                >
                  <SelectTrigger id="subscriptionPlan">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscriptionStatus">Status</Label>
                <Select
                  onValueChange={handleStatusChange}
                  value={editFormData.subscriptionStatus}
                >
                  <SelectTrigger id="subscriptionStatus">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsEditOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesManagement;
